import { sanitizeFileName } from "@/features/attachments/attachments.utils";
import { supabase } from "@/lib/supabase";
import * as Sentry from "@sentry/react";
import type {
  ChangeRequest,
  ChangeRequestUpdate,
  Container,
  CrClassification,
  CrDependencyListRow,
  CrDocumentUpload,
  CrEventListItem,
  CrJiraIssueLink,
  CrLinearIssueLink,
  CrNotionReference,
  CrPriority,
} from "@shared/types/supabase.types";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { parseCrNumberQuery } from "../cr-dependencies";
import { CR_UPLOAD_BUCKET } from "../cr-uploads";
import { uploadToCrUploadsBucket } from "./cr-uploads-storage";

/**
 * CR-147: the columns of `cr_document_uploads` the CR page needs. A file row
 * always carries one; a document row never does.
 *
 * `file_summary` is deliberately absent: it is model output that no surface
 * renders, so fetching it would put untrusted text in the client for nothing.
 * Since CR-212 `authenticated` cannot read it at all (column grants, no table
 * grant): naming it, or `parsed_content`, or a `*` embed fails with 42501.
 * `parse_error` is a short CHECK-constrained enum, fetched since CR-173 so the
 * row can say "Not a valid SVG" for `invalid_svg`.
 */
export type CrLinkedUpload = Pick<
  CrDocumentUpload,
  "storage_path" | "file_name" | "mime_type" | "size_bytes" | "parsing_status" | "parse_error"
>;

/**
 * The two kinds of row the CR's "Linked documents" list holds. A discriminated
 * union rather than `upload: CrLinkedUpload | null`, so `row.upload ? … : …`
 * narrows to a type the file row can take whole — including the `id` and
 * `cr_id` a removal will need.
 */
export type CrLinkedFile = Container & { upload: CrLinkedUpload };
export type CrLinkedDoc = Container & { upload: null };
/** CR-229 S9: the roles a member may give a CR document (the description has its own path). */
export type CrAssignableDocRole = "requirements" | "plan";
export type CrLinkedDocumentListItem = CrLinkedFile | CrLinkedDoc;

/**
 * PostgREST embeds a one-to-one relation as an object, but the shape depends on
 * how it resolves the foreign key — and here the FK column IS the primary key,
 * the case where it has historically returned an array. Normalising once, in
 * the API layer, keeps that ambiguity out of the page and its tests.
 */
type RawLinkedContainerRow = Container & {
  upload: CrLinkedUpload | CrLinkedUpload[] | null;
};

function normalizeLinkedContainerRow(row: RawLinkedContainerRow): CrLinkedDocumentListItem {
  const { upload, ...container } = row;
  const single = (Array.isArray(upload) ? (upload[0] ?? null) : upload) ?? null;
  return single ? { ...container, upload: single } : { ...container, upload: null };
}

/** CR-229: the columns of `cr_jira_issue_links` (the whole row: the section shows most of it). */
export const CR_JIRA_LINK_COLUMNS =
  "id, change_request_id, project_container_id, mapping_id, mapping_name, role, parent_link_id, is_primary, source_kind, source_document_id, source_hash, task_fingerprint, task_position, cloud_id, jira_project_key, jira_issue_id, jira_issue_key, jira_issue_url, jira_issue_type_name, jira_summary, jira_status_name, jira_status_category_key, status_unavailable_reason, last_status_sync_at, last_sent_at, removed_at, created_by, created_at, updated_at";

/** CR-239: the columns of `cr_linear_issue_links` (the whole row: the section shows most of it). */
export const CR_LINEAR_LINK_COLUMNS =
  "id, change_request_id, project_container_id, mapping_id, mapping_name, role, parent_link_id, is_primary, source_kind, source_document_id, source_hash, task_fingerprint, task_position, linear_organization_id, linear_team_key, linear_issue_id, linear_issue_identifier, linear_issue_url, linear_title, linear_state_name, linear_state_type, status_unavailable_reason, last_status_sync_at, last_sent_at, removed_at, created_by, created_at, updated_at";

/**
 * CR-230: the columns of `cr_notion_references` the Notion pages section
 * shows — the whole row, so it matches `CrNotionReference` (CR-273 added the
 * summary state and the worker's scheduling columns).
 */
export const CR_NOTION_REFERENCE_COLUMNS =
  "id, change_request_id, project_container_id, notion_page_id, notion_url, title, icon_emoji, parent_type, parent_id, last_edited_time, child_page_links, created_by, created_at, updated_at, summary_status, summary_text, summary_key_points, summary_generated_at, summary_source_last_edited_time, summary_last_checked_at, summary_error_code, summary_attempts, summary_next_refresh_at, summary_claimed_until, summary_content_truncated, summary_content_block_count";

/** The CR pickers' result cap (searchChangeRequests). */
const CR_SEARCH_LIMIT = 20;

/**
 * CR-273 (D12): ids of the project's live CRs whose attached Notion page
 * summary or key points contain the query, newest attached first, without
 * duplicates. The RPC finds nothing under 2 characters and refuses over 200,
 * so those queries are not sent (the caller has trimmed the query). A failure is reported, never thrown: the
 * summary match only widens the name search. Keep in sync with the AI search-crs copy,
 * backend/api-server/src/lib/cr-notion-summary-search.ts (same bounds, same p_limit, name matches first).
 */
async function searchNotionSummaryCrIds(query: string, projectId: string): Promise<string[]> {
  if (query.length < 2 || query.length > 200) {
    return [];
  }
  const { data, error } = await supabase.rpc("search_cr_notion_summaries", {
    p_project_container_id: projectId,
    p_query: query,
    // The RPC returns one row per matching REFERENCE: ask for its maximum so a
    // CR with many attached pages cannot crowd the other CRs out before dedupe.
    p_limit: 200,
  });
  if (error) {
    Sentry.captureException(error, { tags: { feature: "cr-notion-summary-search" } });
    return [];
  }
  return [...new Set((data ?? []).map((row) => row.change_request_id))];
}

export const changeRequestsApi = {
  /**
   * CR-53: archived CRs are filtered reader-side (`deleted_at IS NULL`), the
   * containers convention — the SELECT policy still exposes them so a future
   * restore feature can read them back. `.single()` therefore errors for an
   * archived id, which is what drives the detail page's not-found state.
   */
  async getChangeRequest(id: string): Promise<ChangeRequest> {
    const { data, error } = await supabase
      .from("change_requests")
      .select("*")
      .eq("id", id)
      .is("deleted_at", null)
      .single();

    if (error) throw error;
    return data;
  },

  async listChangeRequestsForProject(projectId: string): Promise<ChangeRequest[]> {
    const { data, error } = await supabase
      .from("change_requests")
      .select("*")
      .eq("project_container_id", projectId)
      .is("deleted_at", null)
      .order("sequential_id");

    if (error) throw error;
    return data;
  },

  /**
   * The CR pickers' search (link picker, dependency editor): the project's
   * live CRs whose name contains the query, or — CR-148 — whose number it is.
   * CR-273 (D12): CRs whose attached Notion page summary or key points contain
   * the query are found too, through search_cr_notion_summaries (RLS applies).
   *
   * Cap: at most CR_SEARCH_LIMIT rows. Name / number matches come first (by
   * number), then the CRs found ONLY by a summary fill what is left (by number),
   * so a summary hit never pushes a name hit out of the list. A failing summary
   * search is reported and the name results are returned alone.
   */
  async searchChangeRequests(rawQuery: string, projectId: string): Promise<ChangeRequest[]> {
    // CR-273: both searches see the same trimmed text.
    const query = rawQuery.trim();
    const base = supabase
      .from("change_requests")
      .select("*")
      .eq("project_container_id", projectId)
      .is("deleted_at", null);
    // CR-148: `12` / `CR-12` also finds CR-12 by number. Only the parsed digits
    // reach the or() filter string, never the raw query.
    const crNumber = parseCrNumberQuery(query);
    const filtered =
      crNumber === null
        ? base.ilike("name", `%${query}%`)
        : base.or(`name.ilike.%${crNumber}%,sequential_id.eq.${crNumber}`);
    const [byName, summaryCrIds] = await Promise.all([
      filtered.order("sequential_id").limit(CR_SEARCH_LIMIT),
      searchNotionSummaryCrIds(query, projectId),
    ]);

    if (byName.error) throw byName.error;
    const nameMatches = byName.data;
    const seen = new Set(nameMatches.map((cr) => cr.id));
    const extraIds = summaryCrIds.filter((id) => !seen.has(id));
    const room = CR_SEARCH_LIMIT - nameMatches.length;
    if (extraIds.length === 0 || room <= 0) {
      return nameMatches;
    }

    const { data: summaryMatches, error } = await supabase
      .from("change_requests")
      .select("*")
      .eq("project_container_id", projectId)
      .is("deleted_at", null)
      .in("id", extraIds)
      .order("sequential_id")
      .limit(room);
    if (error) {
      Sentry.captureException(error, { tags: { feature: "cr-notion-summary-search" } });
      return nameMatches;
    }
    return [...nameMatches, ...summaryMatches];
  },

  /**
   * CR-148: the live CR numbered `sequentialId` in the project, or null. An
   * exact lookup — the typed-number path must not go through the 20-row name
   * search, where enough name matches could push the CR out of the cap.
   */
  async findChangeRequestByNumber(
    projectId: string,
    sequentialId: number,
  ): Promise<ChangeRequest | null> {
    const { data, error } = await supabase
      .from("change_requests")
      .select("*")
      .eq("project_container_id", projectId)
      .eq("sequential_id", sequentialId)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async updateChangeRequest(id: string, patch: ChangeRequestUpdate): Promise<ChangeRequest> {
    const { data, error } = await supabase
      .from("change_requests")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", id)
      // CR-53: an archived CR is closed to writes on every surface. Without
      // this, a rename or kanban drop racing a colleague's archive would land
      // silently on the archived row (and log a status event on it). Zero rows
      // → .single() errors → the caller's existing error path surfaces it.
      .is("deleted_at", null)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * CR-53: archive (soft-delete) a change request.
   *
   * Replaces the old hard `DELETE`, which was unreachable in practice: deleting
   * the row fired `containers.cr_id ON DELETE SET NULL`, violating
   * `containers_cr_role_requires_cr` (CR-40) for the description document, so
   * every CR with a description failed with 23514 and rolled back.
   *
   * Goes through the SECURITY INVOKER `archive_change_request` RPC: the
   * change_requests policies never mention `deleted_at`, so caller RLS is the
   * boundary (the `delete_document` DEFINER precedent explicitly does NOT
   * apply here — see the migration's "WHY SECURITY INVOKER" section, pinned by
   * a pg_policies pgTAP guard). Member-gated, idempotent (already-archived is
   * a no-op success), constant error for unknown/inaccessible ids, so `error`
   * is authoritative.
   */
  async archiveChangeRequest(id: string): Promise<void> {
    const { error } = await supabase.rpc("archive_change_request", { p_cr_id: id });

    if (error) throw error;
  },

  /**
   * Deliberately counts archived CRs too: `sequential_id` is the human-facing
   * "CR-12" label, so skipping archived rows would hand a fresh CR a number
   * that already appears in event logs, chat names and linked documents.
   */
  async nextSequentialId(projectId: string): Promise<number> {
    const { data: maxRow } = await supabase
      .from("change_requests")
      .select("sequential_id")
      .eq("project_container_id", projectId)
      .order("sequential_id", { ascending: false })
      .limit(1)
      .maybeSingle();

    return (maxRow?.sequential_id ?? 0) + 1;
  },

  async createChangeRequest({
    name,
    classification,
    projectId,
    originChatId,
    sequentialId,
    priority = "none",
  }: {
    name: string;
    classification: CrClassification;
    projectId: string;
    originChatId: string | null;
    sequentialId: number;
    priority?: CrPriority;
  }): Promise<ChangeRequest> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data, error } = await supabase
      .from("change_requests")
      .insert({
        name,
        classification,
        project_container_id: projectId,
        origin_chat_id: originChatId,
        status: "defining",
        priority,
        created_by: user.id,
        sequential_id: sequentialId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * CR-27: the chat rooms linked to a CR, listed for the CR detail page's
   * "Chats" section.
   */
  async listCrLinkedChats(crId: string): Promise<Container[]> {
    const { data, error } = await supabase
      .from("containers")
      .select("*")
      .eq("cr_id", crId)
      .eq("container_type", "room")
      .is("deleted_at", null)
      .order("created_at");

    if (error) throw error;
    return data;
  },

  /**
   * The documents AND uploaded files linked to a CR, for the CR detail page's
   * "Linked documents" section (CR-147).
   *
   * `parsed_content` is deliberately absent from the embed: it holds the whole
   * parsed text of every file (megabytes), and this list only needs to name the
   * file and say whether it is ready. The file's text is never read by the
   * client at all — it exists for the AI context surfaces. Since CR-212 the
   * column is service_role-only: naming it (or a `*` embed) fails with 42501.
   */
  async listCrLinkedDocuments(crId: string): Promise<CrLinkedDocumentListItem[]> {
    const { data, error } = await supabase
      .from("containers")
      .select(
        "*, upload:cr_document_uploads(storage_path, file_name, mime_type, size_bytes, parsing_status, parse_error)",
      )
      .eq("cr_id", crId)
      // CR-147: 'file' containers are CR uploads. They share this list with
      // documents but are never editable, never in the Documents page, and
      // never in search — see the migration for the type-scoped rules.
      .in("container_type", ["document", "file"])
      .is("deleted_at", null)
      .order("created_at");

    if (error) throw error;
    return (data as unknown as RawLinkedContainerRow[])
      .filter(
        // CR-40: the description is a document too, but it already has its own
        // section above — listing it here as well would show it twice. Filtered
        // client-side rather than with `.not("cr_role", "eq", "description")`,
        // which PostgREST turns into `NOT (cr_role = 'description')` — NULL, and
        // therefore excluded, for every ordinary document.
        (row) => row.cr_role !== "description",
      )
      .map(normalizeLinkedContainerRow);
  },

  /**
   * CR-147: upload a file and link it to a CR, the browser → Storage → RPC
   * shape every upload in this repo uses (no api-server route parses
   * multipart). The upload itself is shared with project documentation
   * (CR-181) — see `uploadToCrUploadsBucket` for the ordering and the
   * orphan-removal reasoning.
   */
  async uploadCrDocument({ crId, file }: { crId: string; file: File }): Promise<Container> {
    return uploadToCrUploadsBucket({
      prefixId: crId,
      file,
      link: (storagePath) =>
        supabase.rpc("create_cr_uploaded_document", {
          p_cr_id: crId,
          p_storage_path: storagePath,
        }),
    });
  },

  /**
   * CR-147 (Q5): a short-lived link to the original file. The bucket is
   * private, so this URL is the only way to reach the bytes, and it expires in
   * 60 s.
   *
   * `download` keeps THIS path an attachment rather than an inline render,
   * which matters because `mime_type` is the label the *uploader's* browser put
   * on the bytes. It is not part of the signature, though — anyone holding the
   * URL can drop the parameter — so what actually prevents inline execution is
   * the bucket's `allowed_mime_types` (no text/html). SVG is the one scriptable
   * type allowed (CR-173): the parse route sanitizes and rewrites it, the storage
   * policy refuses it to other members until that is done, and the row never
   * asks for this URL before then. Never add another scriptable type without
   * the same treatment.
   *
   * The name is re-sanitized: the RPC only rejects control and bidi characters,
   * so a row created outside this UI can hold `&` or `#`, which storage-js
   * appends to the URL unencoded.
   */
  async createCrUploadSignedUrl(storagePath: string, fileName: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(CR_UPLOAD_BUCKET)
      .createSignedUrl(storagePath, 60, { download: sanitizeFileName(fileName) });

    if (error) throw error;
    return data.signedUrl;
  },

  /**
   * CR-147: remove an uploaded file from its CR — the mirror of
   * `uploadCrDocument`, in the REVERSE order for the mirror-image reason.
   * Upload puts the bytes first because the INSERT policy refuses a path that
   * is already linked; removal unlinks first because the DELETE policy refuses
   * an object that still backs a live file.
   *
   * So the RPC goes first (it hard-deletes the file container, taking the
   * parsed text with it) and hands back the storage path. Only then can the
   * bytes go.
   *
   * The object removal is best-effort: once the RPC has returned, the file is
   * gone from the CR and from every AI context surface, and reporting a Storage
   * failure here would contradict a list that no longer shows the row. What it
   * leaves behind is an unlinked object its uploader (or a project admin) can
   * still delete — invisible in the UI, so the failure is reported to Sentry
   * rather than swallowed outright.
   */
  async deleteCrUploadedDocument(containerId: string): Promise<void> {
    const { data, error } = await supabase.rpc("delete_cr_uploaded_document", {
      p_container_id: containerId,
    });

    if (error) throw error;

    // `storage_path` is NOT NULL, so the RPC always returns a path on success;
    // the guard only covers a client whose generated types are ahead of the DB.
    if (!data) return;

    const { error: removeError } = await supabase.storage.from(CR_UPLOAD_BUCKET).remove([data]);
    if (removeError) {
      Sentry.captureException(removeError, {
        tags: { feature: "cr-uploads" },
        extra: { storagePath: data },
      });
    }
  },

  async setDocCrId(documentId: string, crId: string | null): Promise<void> {
    const { error } = await supabase
      .from("containers")
      .update({ cr_id: crId })
      .eq("id", documentId);

    if (error) throw error;
  },

  /**
   * Link a chat container to a CR via containers.cr_id (CR-27).
   *
   * Goes through a SECURITY DEFINER RPC because clients cannot UPDATE room
   * containers directly — the only client-facing containers UPDATE policy is
   * restricted to documents, so a direct update on a room silently affects zero
   * rows under RLS. The RPC raises on any failure, so `error` is authoritative.
   */
  async linkChatToCr(chatId: string, crId: string): Promise<void> {
    const { error } = await supabase.rpc("link_chat_to_cr", {
      p_chat_id: chatId,
      p_cr_id: crId,
    });

    if (error) throw error;
  },

  /**
   * Set or clear the CR a chat is linked to (CR-149 D1). `crId: null` unlinks.
   *
   * A second RPC rather than a reuse of `linkChatToCr`: that one is the chat
   * *creation* path ("Start chat with AI") and deliberately refuses both null
   * and re-linking a chat another CR already owns (`chat_already_linked`), which
   * is exactly what this user-driven control has to be able to do. Like it, it
   * cannot be a direct `containers` update — the only client-facing UPDATE
   * policy covers document containers, so a room update matches zero rows under
   * RLS and looks like a success.
   *
   * Member-gated server-side; an unknown, archived, cross-project or otherwise
   * inaccessible CR and an unknown or inaccessible chat all share one constant
   * 42501 message, so `error` is authoritative and says nothing about which it
   * was. Branch on `error.code`, never on the message.
   */
  async setChatCrId(chatId: string, crId: string | null): Promise<void> {
    const { error } = await supabase.rpc("set_chat_cr_id", {
      p_chat_id: chatId,
      p_cr_id: crId,
    });

    if (error) throw error;
  },

  /**
   * CR-84: create an ordinary (role-less) document already linked to a CR, in
   * one atomic operation — deliberately NOT `create_document` followed by
   * `setDocCrId`, which would leave an orphan unlinked document on failure and
   * log the wrong CR events. The full rationale, the guards and the field
   * decisions live in the migration:
   * `backend/supabase/migrations/20260910110000_cr84_create_cr_linked_document.sql`.
   *
   * Member-gated server-side; unknown, archived and inaccessible CRs share one
   * constant 42501 message, so `error` is authoritative and says nothing about
   * which of the three it was.
   */
  /**
   * CR-229 S9: make a CR-linked document the CR's Definition ("requirements")
   * or Technical Plan ("plan"), or clear its role (null). The RPC moves the
   * role off the CR's previous holder and checks the caller is a
   * non-contributor member of the CR's project.
   */
  async setCrDocumentRole({
    documentId,
    role,
  }: {
    documentId: string;
    role: CrAssignableDocRole | null;
  }): Promise<Container> {
    const { data, error } = await supabase.rpc("set_cr_document_role", {
      p_document_id: documentId,
      p_role: role,
    });

    if (error) throw error;
    return data;
  },

  async createCrLinkedDocument({ crId, name }: { crId: string; name: string }): Promise<Container> {
    const { data, error } = await supabase.rpc("create_cr_linked_document", {
      p_cr_id: crId,
      p_name: name,
    });

    if (error) throw error;
    return data;
  },

  /**
   * CR-22: the event log for a CR. Goes through a SECURITY DEFINER RPC
   * (access = is_container_member on the CR's project, the CR page rule) that
   * joins profiles for actor display fields at read time.
   *
   * CR-105: the RPC returns rows NEWEST FIRST, which is the order the feed
   * renders. Ordering is the RPC's contract on purpose — a consumer that
   * re-sorts here or in a selector would hide it from both the wrapper and the
   * renderer — so callers must pass the rows through untouched.
   */
  async listCrEvents(crId: string): Promise<CrEventListItem[]> {
    const { data, error } = await supabase.rpc("list_cr_events", {
      p_change_request_id: crId,
    });

    if (error) throw error;
    return data;
  },

  /** CR-148: refusals come back as the RPC's exception key — see crDependencyErrorReason. */
  async addCrDependency(crId: string, dependsOnId: string): Promise<void> {
    const { error } = await supabase.rpc("add_cr_dependency", {
      p_change_request_id: crId,
      p_depends_on_id: dependsOnId,
    });

    if (error) throw error;
  },

  async removeCrDependency(crId: string, dependsOnId: string): Promise<void> {
    const { error } = await supabase.rpc("remove_cr_dependency", {
      p_change_request_id: crId,
      p_depends_on_id: dependsOnId,
    });

    if (error) throw error;
  },

  /**
   * CR-148: every dependency row of a CR, active and historical, oldest first
   * (the RPC's order). `is_active` is computed server-side at read time (not
   * removed, dependency neither done nor archived) — consumers filter on it
   * rather than re-deriving the rule. Members only (42501 otherwise).
   */
  async listCrDependencies(crId: string): Promise<CrDependencyListRow[]> {
    const { data, error } = await supabase.rpc("list_cr_dependencies", {
      p_change_request_id: crId,
    });

    if (error) throw error;
    return data ?? [];
  },

  /**
   * CR-230: the CR's Notion page references, oldest first (attach order).
   * Read under the caller's RLS (non-contributor project members). Attaching
   * goes through the api-server (features/notion), never a direct insert.
   */
  async listCrNotionReferences(crId: string): Promise<CrNotionReference[]> {
    const { data, error } = await supabase
      .from("cr_notion_references")
      .select(CR_NOTION_REFERENCE_COLUMNS)
      .eq("change_request_id", crId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data ?? []) as CrNotionReference[];
  },

  /**
   * CR-229: the CR's Jira issue links (parents and children, removed children
   * included), oldest first. Member RLS read; the statuses are the stored
   * snapshot — the section asks the api-server to refresh the stale ones (D6).
   */
  async listCrJiraLinks(crId: string): Promise<CrJiraIssueLink[]> {
    const { data, error } = await supabase
      .from("cr_jira_issue_links")
      .select(CR_JIRA_LINK_COLUMNS)
      .eq("change_request_id", crId)
      .order("created_at", { ascending: true })
      .order("id", { ascending: true });

    if (error) throw error;
    return (data ?? []) as CrJiraIssueLink[];
  },

  /**
   * CR-239: the CR's Linear issue links (parents and children, removed
   * children included), oldest first. Member RLS read; the states are the
   * stored snapshot — the section asks the api-server to refresh stale ones.
   */
  async listCrLinearLinks(crId: string): Promise<CrLinearIssueLink[]> {
    const { data, error } = await supabase
      .from("cr_linear_issue_links")
      .select(CR_LINEAR_LINK_COLUMNS)
      .eq("change_request_id", crId)
      .order("created_at", { ascending: true })
      .order("id", { ascending: true });

    if (error) throw error;
    return (data ?? []) as CrLinearIssueLink[];
  },

  /** CR-230 (Q4): any project member removes a reference; the RPC decides and logs it. */
  async removeCrNotionReference(referenceId: string): Promise<void> {
    const { error } = await supabase.rpc("remove_cr_notion_reference", {
      p_reference_id: referenceId,
    });

    if (error) throw error;
  },
};

/**
 * CR-22: live updates for the CR event log. Client writes are append-only, but
 * since the CR-69 follow-up the api-server UPDATEs freshly inserted trigger
 * rows to stamp MCP attribution (stampMcpCrEvents) — an INSERT-only
 * subscription would leave live viewers on the un-stamped "Kairo" row until an
 * unrelated refresh, so UPDATE is included. The SUBSCRIBED callback re-syncs
 * events that landed between the initial fetch and the subscription (same
 * pattern as createTasksSubscription).
 */
export function createCrEventsSubscription(crId: string, callback: () => void): RealtimeChannel {
  return supabase
    .channel(`cr-events-${crId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "cr_events",
        filter: `change_request_id=eq.${crId}`,
      },
      callback,
    )
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "cr_events",
        filter: `change_request_id=eq.${crId}`,
      },
      callback,
    )
    .subscribe((status) => {
      if (status === "SUBSCRIBED") callback();
    });
}

export function removeCrEventsSubscription(channel: RealtimeChannel): void {
  void supabase.removeChannel(channel);
}
